import { useCallback, useContext, useRef } from 'react';
import GeneralContext from '../context/GeneralContext';
import { getEnvironmentApiUrl } from '../services/General';
import { getRefreshToken } from '../services/Auth';
import { IRefreshLogin, LoginRequest } from '../interfaces/UserInfo';

let refreshTokenPromise: Promise<{ accessToken: string, firebaseToken: string, hospitalToken: string }> | null = null;

const useAccessToken = () => {
    const { setAccessToken, setFirebaseToken, setHospitalToken, accessToken } = useContext(GeneralContext);
    const latestAccessToken = useRef(accessToken);

    latestAccessToken.current = accessToken;

    const sendRequest = useCallback(async () => {
        if (refreshTokenPromise) {
            return refreshTokenPromise;
        }

        refreshTokenPromise = (async () => {
            try {
                if (window.config.THIRD_PARTY_LOGIN === false) {
                    const response = await fetch(getEnvironmentApiUrl() + 'login/refresh_token', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', api_key: 'digitomedica' },
                        body: JSON.stringify({ 'refresh_token': getRefreshToken() }),
                    });

                    if (!response.ok) {
                        if (response.status === 401) window.location.href = '/logout';
                        throw new Error('Request failed!');
                    }

                    const data: IRefreshLogin = await response.json();
                    localStorage.setItem('user', JSON.stringify(data.user));
                    localStorage.setItem('refresh-token', data.refreshToken);
                    localStorage.setItem('firebase-token', data.firebase_token);
                    localStorage.setItem('refresh-token-expiration-date', data.refreshTokenExpTime);
                    latestAccessToken.current = data.authToken;
                    setAccessToken(data.authToken);
                    setFirebaseToken(data.firebase_token);

                    return {
                        accessToken: data.authToken,
                        firebaseToken: data.firebase_token,
                        hospitalToken: ''
                    };
                } else {
                    const refreshToken = getRefreshToken();
                    if (!refreshToken) throw new Error('Missing hospital refresh token');

                    const response = await fetch(getEnvironmentApiUrl() + 'login/refreshToken/' + refreshToken, {
                        method: 'GET',
                        headers: { 'Content-Type': 'application/json', api_key: 'digitomedica' }
                    });

                    if (!response.ok) {
                        if (response.status === 401) window.location.href = '/logout';
                        throw new Error('Request failed!');
                    }

                    const data: LoginRequest = await response.json();
                    localStorage.setItem('user', JSON.stringify(data.result.response.user));
                    localStorage.setItem('refresh-token', data.result.refresh_token);
                    localStorage.setItem('firebase-token', data.result.firebase_token);
                    localStorage.setItem('refresh-token-expiration-date', data.result.refresh_exp_time);

                    const hospitalData = {
                        hospital_refresh_token: data.result.response.refreshToken,
                        hospital_token: data.result.response.token,
                    };
                    localStorage.setItem('hospitalData', JSON.stringify(hospitalData));
                    latestAccessToken.current = data.result.token;
                    setAccessToken(data.result.token);
                    setHospitalToken(data.result.response.token);

                    return {
                        accessToken: data.result.token,
                        firebaseToken: data.result.firebase_token,
                        hospitalToken: data.result.response.token
                    };
                }
            } finally {
                refreshTokenPromise = null;
            }
        })();

        return refreshTokenPromise;
    }, [setAccessToken, setFirebaseToken, setHospitalToken]);

    return { getAccessToken: sendRequest, latestAccessToken };
};

export default useAccessToken;